import type { BulkDeliveryResponse } from "~/entities/BulkDeliveryResponse";
import { Delivery, toDeliveryConditionSeed, type DeliveryEmail } from "~/entities/Delivery";
import type { DeliveryMailTemplateResponse } from "~/entities/DeliveryMailTemplateResponse";
import type { StepDeliveryResponse } from "~/entities/StepDeliveryResponse";
import type { UserTagTypeResponse } from "~/entities/UserTagTypeResponse";
import type {
  DeliveryStatus,
  IDeliveryRepository,
  IDeliverySaveBody,
  IDeliverySaveBodyCondition,
  IDeliveryTestDeliveryBody,
} from "~/interfaces/IDeliveryRepository";

export class DeliveryRepository implements IDeliveryRepository {
  async getBulkDeliveries(options?: Record<string, any>): Promise<BulkDeliveryResponse> {
    const paramsObj: { [name: string]: string } = {
      ...filterEmptyObject(options || {}),
    };
    const searchParams = new URLSearchParams(paramsObj);
    const res = await GetRequest<BulkDeliveryResponse>(`/delivery/bulk?${searchParams.toString()}`);
    if (!res || res.statusCode !== 200) {
      throw new Error(`DeliveryRepository.getDeliveries API Error: ${JSON.stringify(res)}`);
    }
    return res;
  }

  async getStepDeliveries(options?: Record<string, any>): Promise<StepDeliveryResponse> {
    const paramsObj: { [name: string]: string } = {
      ...filterEmptyObject(options || {}),
    };
    const searchParams = new URLSearchParams(paramsObj);
    const res = await GetRequest<StepDeliveryResponse>(`/delivery/step?${searchParams.toString()}`);
    if (!res || res.statusCode !== 200) {
      throw new Error(`DeliveryRepository.getDeliveries API Error: ${JSON.stringify(res)}`);
    }
    return res;
  }

  async changeDeliveryStatus(deliveryId: string, status: DeliveryStatus): Promise<void> {
    const res = await PutRequest<{ statusCode: number }>(
      `/delivery/${deliveryId}/status`,
      JSON.stringify({ status }),
      true,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error(`DeliveryRepository.changeDeliveryStatus API Error: ${JSON.stringify(res)}`);
    }
  }

  async getDelivery(deliveryId: string, options?: Record<string, any>): Promise<Delivery> {
    const paramsObj: { [name: string]: string } = {
      ...filterEmptyObject(options || {}),
    };
    const searchParams = new URLSearchParams(paramsObj);
    const res = await GetRequest<Delivery>(`/delivery/${deliveryId}?${searchParams.toString()}`);
    if (!res || res.statusCode !== 200) {
      throw new Error(`DeliveryRepository.getDelivery API Error: ${JSON.stringify(res)}`);
    }
    return res;
  }

  async createDelivery(body: IDeliverySaveBody): Promise<void> {
    const res = await PostRequest<{ statusCode: number }>(`/delivery`, JSON.stringify(body), true);
    if (!res || res.statusCode !== 200) {
      throw new Error(`DeliveryRepository.createDelivery API Error: ${JSON.stringify(res)}`);
    }
  }

  async updateDelivery(deliveryId: string, body: IDeliverySaveBody): Promise<void> {
    const res = await PutRequest<{ statusCode: number }>(`/delivery/${deliveryId}`, JSON.stringify(body), true);
    if (!res || res.statusCode !== 200) {
      throw new Error(`DeliveryRepository.updateDelivery API Error: ${JSON.stringify(res)}`);
    }
  }

  async deleteDelivery(deliveryId: string): Promise<void> {
    const res = await DeleteRequest<{ statusCode: number }>(`/delivery/${deliveryId}`);
    if (!res || res.statusCode !== 200) {
      throw new Error(`DeliveryRepository.deleteDelivery API Error: ${JSON.stringify(res)}`);
    }
  }

  async getAccountPlans(): Promise<{ planId: string; planTitle: string }[]> {
    const res = await GetRequest<{ statusCode: number; accountPlans: { planId: string; planTitle: string }[] }>(
      `/delivery/account_plans`,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to put publish");
    }
    return res.accountPlans;
  }

  async getMailTemplates(): Promise<DeliveryEmail[]> {
    const res = await GetRequest<DeliveryMailTemplateResponse>(`/delivery/mail_template?limit=100&offset=0`);
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to get mail templates");
    }
    return res.deliveryMessages;
  }

  async getTagTypes(): Promise<UserTagTypeResponse> {
    const tags = await GetRequest<UserTagTypeResponse>("/delivery/type");
    return tags;
  }

  async testDelivery(body: IDeliveryTestDeliveryBody): Promise<void> {
    const res = await PostRequest<{ statusCode: number }>(`/delivery/test`, JSON.stringify(body), true);
    if (!res || res.statusCode !== 200) {
      throw new Error(`DeliveryRepository.testDelivery API Error: ${JSON.stringify(res)}`);
    }
  }

  async getUserCount(conitions: IDeliverySaveBodyCondition[]): Promise<{ userCount: number }> {
    const body = conitions.map(condition => {
      return toDeliveryConditionSeed(condition);
    });
    const res = await PostRequest<{ statusCode: number; userCount: number }>(
      `/delivery/user_count`,
      JSON.stringify({ conditions: body }),
      true,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error(`DeliveryRepository.getUserCount API Error: ${JSON.stringify(res)}`);
    }
    return res;
  }
}
